import { createAsyncThunk, createSelector } from '@reduxjs/toolkit';

import { percentToBps } from '@talos/kyoko';
import type { AppExtraArgument, AppState } from '../../../../providers/AppStateProvider/types';
import { type EffectivePricingRuleRequestParams, getEffectivePricingRuleURL, salesRFQApi } from '../SalesRFQApi';

/**
 * Returns the current parameters for making a effective pricing rule request in the current state of the sales rfq form
 * If the current state is in any way not supportive of effective pricing rule, this selector will return undefined
 */
export const selectEffectivePricingRuleRequestParams = createSelector(
  (state: AppState) => state.salesRFQ.form.symbolField,
  (state: AppState) => state.salesRFQ.form.customerField,
  (symbolField, customerField) => {
    if (!symbolField.value?.Symbol || !customerField.value?.Name) {
      return undefined;
    }

    return {
      counterparty: customerField.value.Name,
      symbol: symbolField.value.Symbol,
    } satisfies EffectivePricingRuleRequestParams;
  }
);

/** Selects the currently getEffectivePricingRule response with the current request params state */
export const selectEffectivePricingRuleResponse = createSelector(
  selectEffectivePricingRuleRequestParams,
  (state: AppState) => state,
  (requestParams, state) => {
    if (!requestParams) {
      return undefined;
    }

    return salesRFQApi.endpoints.getEffectivePricingRule.select(requestParams)(state);
  }
);

/**
 * Selects the EffectivePricingRule for the first market account selected in the sales rfq form.
 */
export const selectEffectivePricingRule = createSelector(selectEffectivePricingRuleResponse, response => {
  return response?.data?.at(0);
});

/**
 * A call to just invalidate and re-get the effective pricing rule given the current state of the sales rfq form. If the current
 * state is not supportive of making a request for effective pricing rule, the function will just return early and not do anything.
 */
export const refreshEffectivePricingRule = createAsyncThunk<
  void,
  undefined,
  { state: AppState; extra: AppExtraArgument }
>('salesRFQApi/refreshEffectivePricingRuleThunk', (_, thunk) => {
  const requestParams = selectEffectivePricingRuleRequestParams(thunk.getState());
  if (!requestParams) {
    return;
  }
  // Before refreshing the request, we need to invalidate the current request (if any). Otherwise a new request would not be made.
  thunk.dispatch(
    salesRFQApi.util.invalidateTags([{ type: 'EffectivePricingRule', id: getEffectivePricingRuleURL(requestParams) }])
  );
  thunk.dispatch(salesRFQApi.endpoints.getEffectivePricingRule.initiate(requestParams));
});

export const selectCanChangeSpreadOnOpenQuote = createSelector(
  selectEffectivePricingRule,
  maybeEffectivePricingRule => {
    // [DEAL-5579]
    if (maybeEffectivePricingRule == null) {
      return true;
    }
    if (maybeEffectivePricingRule.TakeProfitInQuoteCurrency === true) {
      return false;
    }
    return true;
  }
);

export const selectCanOnlyEditPrice = createSelector(selectEffectivePricingRule, maybeEffectivePricingRule => {
  return maybeEffectivePricingRule?.TakeProfitInQuoteCurrency === true;
});

export const selectOfferSpreadFieldDisplayValue = createSelector(
  selectEffectivePricingRule,
  (state: AppState) => state.salesRFQ.form.offerSpreadField,
  (state: AppState) => state.salesRFQ.currentQuote?.EffectiveOfferSpread,
  (maybeEffectivePricingRule, offerSpreadField, effectiveOfferSpread) => {
    if (offerSpreadField.displayValue || offerSpreadField.isFocused) {
      return offerSpreadField.displayValue;
    }
    if (effectiveOfferSpread) {
      return percentToBps(effectiveOfferSpread);
    }
    return percentToBps(maybeEffectivePricingRule?.OfferSpread) || '';
  }
);

export const selectBidSpreadFieldDisplayValue = createSelector(
  selectEffectivePricingRule,
  (state: AppState) => state.salesRFQ.form.bidSpreadField,
  (state: AppState) => state.salesRFQ.currentQuote?.EffectiveBidSpread,
  (maybeEffectivePricingRule, bidSpreadField, effectiveBidSpread) => {
    if (bidSpreadField.displayValue || bidSpreadField.isFocused) {
      return bidSpreadField.displayValue;
    }
    if (effectiveBidSpread) {
      return percentToBps(effectiveBidSpread);
    }
    return percentToBps(maybeEffectivePricingRule?.BidSpread) || '';
  }
);
